import { ApolloClient, InMemoryCache, Observable } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { createUploadLink } from "apollo-upload-client";
import { onError } from "@apollo/client/link/error";
import { API_URL } from "../Utils/utils";
import Pusher from "pusher-js";
import PusherLink from "./PusherLink";
import { ApolloLink } from "@apollo/client/core";
import {
  getAuthGeneration,
  getAuthToken,
  getCsrfToken,
  redirectSuspendedAccount,
  resetCsrfToken,
} from "./tokenUtils";
import Cookies from "js-cookie";

const isLocalOnline = process.env.REACT_APP_ENV === "local-online";

const selectedTeamUserId = () => {
  try {
    return window.localStorage.getItem("currentTeamUser") || undefined;
  } catch (error) {
    return undefined;
  }
};

// Create the upload link
const uploadLink = createUploadLink({
  uri: API_URL + "/graphql",
  credentials: isLocalOnline ? "same-origin" : "include",
});

// Authorization logic using both cookies and localStorage
const authLink = setContext(async (_, { headers }) => {
  if (window.location !== window.parent.location) {
    // Public embeds omit bearer authentication, but cookie sessions still need CSRF.
    if (!isLocalOnline) {
      await getCsrfToken();
    }
    const xsrfToken = Cookies.get('XSRF-TOKEN');
    return {
      headers: {
        ...headers,
        "X-XSRF-TOKEN": xsrfToken ? decodeURIComponent(xsrfToken) : undefined,
        "X-Client-Auth-Generation": getAuthGeneration(),
        "X-Team-User-Id": selectedTeamUserId(),
        "X-Requested-With": "XMLHttpRequest",
        Accept: "application/json",
      },
    };
  } else {
    if (isLocalOnline) {
      // Token-based authentication
      const token = getAuthToken(); // Retrieve token from storage
      if (!token) {
        throw new Error("No authentication token found. Please login.");
      }
      return {
        headers: {
          ...headers,
          "X-Client-Auth-Generation": getAuthGeneration(),
          "X-Team-User-Id": selectedTeamUserId(),
          Authorization: `Bearer ${token}`,
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
      };
    } else {
      // Session-based authentication
      await getCsrfToken(); // Ensure CSRF token is loaded
      const token = getAuthToken();
      const xsrfToken = Cookies.get("XSRF-TOKEN");
      return {
        headers: {
          ...headers,
          "X-Client-Auth-Generation": getAuthGeneration(),
          "X-Team-User-Id": selectedTeamUserId(),
          "X-XSRF-TOKEN": xsrfToken ? decodeURIComponent(xsrfToken) : undefined,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
      };
    }
  }
});

const buildPusherAuthHeaders = () => {
  const token = getAuthToken();
  const baseHeaders = {
    "X-Requested-With": "XMLHttpRequest",
    Accept: "application/json",
  };

  if (isLocalOnline) {
    return token
      ? {
          ...baseHeaders,
          Authorization: `Bearer ${token}`,
        }
      : null;
  }

  const xsrfToken = Cookies.get("XSRF-TOKEN");
  const decodedXsrf = xsrfToken ? decodeURIComponent(xsrfToken) : "";
  if (!decodedXsrf && !token) {
    return null;
  }

  return {
    ...baseHeaders,
    ...(decodedXsrf ? { "X-XSRF-TOKEN": decodedXsrf } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const pusherAuthHeaders = buildPusherAuthHeaders();
const canUsePusher =
  Boolean(process.env.REACT_APP_PUSHER_API_KEY) &&
  Boolean(process.env.REACT_APP_PUSHER_CLUSTER) &&
  Boolean(pusherAuthHeaders);

const pusherLink = canUsePusher
  ? new PusherLink({
      pusher: new Pusher(process.env.REACT_APP_PUSHER_API_KEY, {
        cluster: process.env.REACT_APP_PUSHER_CLUSTER,
        authEndpoint: `${process.env.REACT_APP_BACKEND_URL}/graphql/subscriptions/auth`,
        auth: {
          headers: pusherAuthHeaders,
        },
      }),
    })
  : null;

// Error handling link
const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (
      graphQLErrors?.some(
        (error) => error.extensions?.code === "ACCOUNT_SUSPENDED",
      )
    ) {
      const requestGeneration = String(
        operation.getContext()?.headers?.["X-Client-Auth-Generation"] ?? "",
      );

      if (requestGeneration === getAuthGeneration()) {
        redirectSuspendedAccount();
      }
    }

    if (
      networkError &&
      (networkError.statusCode === 419 || networkError.response?.status === 419)
    ) {
      return new Observable((observer) => {
        resetCsrfToken()
          .then(() => {
            const subscriber = {
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer),
            };

            forward(operation).subscribe(subscriber);
          })
          .catch((error) => {
            observer.error(error);
          });
      });
    }
  },
);

// Combine the links in the correct order
const link = ApolloLink.from(
  [errorLink, authLink, pusherLink, uploadLink].filter(Boolean),
);

// Create the Apollo Client instance
const graphqlClient = new ApolloClient({
  link,
  cache: new InMemoryCache(),
});
// const graphqlClient = new ApolloClient({
//   uri: API_URL + "/graphql",
//   cache: new InMemoryCache(),
//   headers: {
//     authorization: apiToken ? `Bearer ${apiToken}` : "",
//   },
// });
export default graphqlClient;
